<clp-toolbar [title]="('subscription.title' | translate)">
    <a
      [routerLink]="['/createSubscription']"
      routerLinkActive="activeClass"
      class="clp-btn clp-btn-warning clp-btn-sm inline-flex items-center gap-2">
      <i class="fa-solid fa-plus"></i>
      <span>{{'general.create' | translate}}</span>
    </a>
</clp-toolbar>

<div class="px-4 py-3">
  @if (subscriptions$ | async; as subscriptions) {
    <div class="mb-3 grid grid-cols-2 gap-2 lg:grid-cols-4">
      <div class="rounded-lg border border-slate-700 bg-slate-900/55 p-2.5">
        <p class="text-xs uppercase tracking-wide text-slate-400">{{'subscription.title' | translate}}</p>
        <p class="mt-1 text-xl font-semibold text-slate-100">{{ subscriptions.length }}</p>
      </div>
      <div class="rounded-lg border border-slate-700 bg-slate-900/55 p-2.5">
        <p class="text-xs uppercase tracking-wide text-slate-400">{{ 'subscription.visibility.1' | translate }}</p>
        <p class="mt-1 text-xl font-semibold text-slate-200">{{ getCountByVisibility(subscriptions, 1) }}</p>
      </div>
      <div class="rounded-lg border border-slate-700 bg-slate-900/55 p-2.5">
        <p class="text-xs uppercase tracking-wide text-slate-400">{{ 'subscription.visibility.2' | translate }}</p>
        <p class="mt-1 text-xl font-semibold text-emerald-300">{{ getCountByVisibility(subscriptions, 2) }}</p>
      </div>
      <div class="rounded-lg border border-slate-700 bg-slate-900/55 p-2.5">
        <p class="text-xs uppercase tracking-wide text-slate-400">{{ 'subscription.visibility.3' | translate }}</p>
        <p class="mt-1 text-xl font-semibold text-amber-300">{{ getCountByVisibility(subscriptions, 3) }}</p>
      </div>
    </div>

    <div class="mb-3 rounded-lg border border-slate-700 bg-slate-900/55 p-2.5">
      <div class="grid gap-2 md:grid-cols-[minmax(0,1fr)_200px]">
        <div>
          <label class="mb-1 block text-xs uppercase tracking-wide text-slate-400">{{ 'subscription.name' | translate }}</label>
          <div class="relative">
            <i class="fa-solid fa-magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-500"></i>
            <input
              type="text"
              class="clp-input pl-9"
              [value]="searchTerm"
              (input)="onSearchChange($event)"
              [attr.placeholder]="'subscription.searchPlaceholder' | translate" />
          </div>
        </div>
        <div>
          <label class="mb-1 block text-xs uppercase tracking-wide text-slate-400">{{ 'subscription.visibilityLabel' | translate }}</label>
          <select class="clp-select" [value]="visibilityFilter" (change)="onVisibilityFilterChange($event)">
            <option value="all">{{ 'subscription.allVisibilities' | translate }}</option>
            <option value="1">{{ 'subscription.visibility.1' | translate }}</option>
            <option value="2">{{ 'subscription.visibility.2' | translate }}</option>
            <option value="3">{{ 'subscription.visibility.3' | translate }}</option>
          </select>
        </div>
      </div>
    </div>

    @let filteredSubscriptions = getFilteredSubscriptions(subscriptions);

    @if (subscriptions.length === 0) {
      <div class="clp-card">
        <div class="clp-card-body flex flex-col items-center gap-3 py-10 text-center">
          <i class="fa-solid fa-box-open text-3xl text-slate-500"></i>
          <p class="text-sm text-slate-300">{{ 'subscription.noSubscriptions' | translate }}</p>
          <a [routerLink]="['/createSubscription']" class="clp-btn clp-btn-warning clp-btn-sm">{{'general.create' | translate}}</a>
        </div>
      </div>
    } @else if (filteredSubscriptions.length === 0) {
      <div class="clp-card">
        <div class="clp-card-body flex flex-col items-center gap-2 py-8 text-center">
          <i class="fa-solid fa-filter-circle-xmark text-2xl text-slate-500"></i>
          <p class="text-sm text-slate-300">{{ 'subscription.noMatchingSubscriptions' | translate }}</p>
        </div>
      </div>
    } @else {
      <div class="space-y-2">
        @for (subscription of filteredSubscriptions; track subscription.id ?? $index) {
          <article class="group rounded-lg border border-slate-700 bg-slate-900/55 px-3 py-2.5 shadow-sm transition hover:border-amber-400/40 hover:bg-slate-900/75">
            <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div class="min-w-0 space-y-1">
                <h3 class="truncate text-sm font-semibold text-slate-100">{{ subscription.name }}</h3>
                @if (subscription.description) {
                  <p class="truncate text-xs text-slate-400">{{ subscription.description }}</p>
                }
                <p class="text-xs text-slate-300">
                  @if (isRecurring()) {
                    {{ subscription.recurringFee ?? 0 | number:'1.2-2' }} {{ subscription.currency ?? 'USD' }}
                    /
                    {{ subscription.recurringIntervalCount ?? 1 }}
                    {{ getRecurringIntervalLabelKey(subscription.recurringInterval) | translate | lowercase }}
                  } @else {
                    {{ subscription.oneTimeFee ?? 0 | number:'1.2-2' }} {{ subscription.currency ?? 'USD' }}
                  }
                </p>
              </div>

              <div class="flex shrink-0 items-center gap-1.5">
                <span
                  class="rounded-full border px-2 py-0.5 text-[11px] font-semibold"
                  [ngClass]="getVisibilityBadgeClass(subscription.visibility)">
                  {{ getVisibilityLabelKey(subscription.visibility) | translate }}
                </span>

                <span
                  class="rounded-full border px-2 py-0.5 text-[11px] font-semibold"
                  [ngClass]="getBillingTypeBadgeClass()">
                  {{ getBillingTypeLabelKey() | translate }}
                </span>

                <button
                  class="clp-btn clp-btn-warning clp-btn-sm"
                  (click)="editSubscription(subscription.id)"
                  [attr.aria-label]="'general.edit' | translate"
                  [title]="'general.edit' | translate">
                  <i class="fa-regular fa-pen-to-square text-xs"></i>
                </button>

                <button
                  class="clp-btn clp-btn-danger clp-btn-sm"
                  (click)="openDeleteConfirmation(subscription)"
                  [attr.aria-label]="'general.delete' | translate"
                  [title]="'general.delete' | translate">
                  <i class="fa-solid fa-trash-can text-xs"></i>
                </button>
              </div>
            </div>
          </article>
        }
      </div>
    }
  }
</div>

@if (subscriptionToDelete) {
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div
      class="absolute inset-0 bg-slate-950/70"
      (click)="closeDeleteConfirmation()"
      aria-hidden="true">
    </div>

    <section class="relative w-full max-w-md rounded-xl border border-slate-700 bg-slate-900 p-5 shadow-2xl">
      <div class="mb-4 flex items-start gap-3">
        <span class="flex h-9 w-9 items-center justify-center rounded-full border border-red-500/40 bg-red-500/15 text-red-300">
          <i class="fa-solid fa-triangle-exclamation text-sm"></i>
        </span>
        <div class="min-w-0">
          <h3 class="text-base font-semibold text-slate-100">
            {{ 'subscription.delete.confirmTitle' | translate }}
          </h3>
          <p class="mt-1 text-sm text-slate-300">
            {{ 'subscription.delete.confirmMessage' | translate:{ name: (subscriptionToDelete.name ?? '') } }}
          </p>
        </div>
      </div>

      <div class="flex justify-end gap-2">
        <button
          type="button"
          class="clp-btn clp-btn-secondary"
          [disabled]="deleting"
          (click)="closeDeleteConfirmation()">
          {{ 'general.cancel' | translate }}
        </button>
        <button
          type="button"
          class="clp-btn clp-btn-danger"
          [disabled]="deleting"
          (click)="confirmDeleteSubscription()">
          {{ 'general.delete' | translate }}
        </button>
      </div>
    </section>
  </div>
}
